/**
 * ItemChecklist - Componente individual de item do checklist
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent } from '../ui/dialog';
import { DollarSign, Package, Wrench, Trash2, Camera, X, ZoomIn, ShoppingCart, FileText, GripVertical, ChevronDown, ChevronRight, Paperclip, Link2 } from 'lucide-react';
import { TIPOS_ITEM, getCustoItem, formatCurrency } from './constantes';
import ImageUpload from '../ImageUpload';
import { CurrencyInput } from '../ui/currency-input';
import { processImageUrl } from '../../utils/imageConverter';
import EstoqueStatus from './EstoqueStatus';
import FornecedorCombobox from '../FornecedorCombobox';

const ItemChecklist = ({
  item,
  index,
  fornecedores,
  podeVerCustos,
  onUpdateItem,
  onRemoveItem,
  onDragStartItem,
  onDragOverItem,
  onDragLeaveItem,
  onDropItem,
  dragOverIndex,
  onOpenAddProduto,
  onUpdateItemCusto,
  onRemoveItemCusto,
  onFornecedoresUpdate,
  projeto,
  embarcacaoIndex,
  onOpenSolicitarCompra
}) => {
  const [imagemAmpliada, setImagemAmpliada] = useState(null);
  const [showTipoMenu, setShowTipoMenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const tipoMenuRef = useRef(null);
  const tipoInfo = TIPOS_ITEM[item.tipo_item] || TIPOS_ITEM.especificacao;
  const TipoIcon = tipoInfo.icon;
  const custoItem = getCustoItem(item);
  const itensCusto = Array.isArray(item.itens_custo) ? item.itens_custo : [];
  const fornecedorSel = fornecedores.find((f) => f.id === item.fornecedor_id);
  const temFoto = !!item.foto_url;

  // Fechar menu ao clicar fora
  useEffect(() => {
    if (!showTipoMenu) return;
    const handleClick = (e) => {
      if (tipoMenuRef.current && !tipoMenuRef.current.contains(e.target)) {
        setShowTipoMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTipoMenu]);

  // Helper para construir URL da imagem - usa função centralizada
  const getImageUrl = (url) => {
    return processImageUrl(url);
  };

  return (
    <div
      className={`p-4 hover:bg-gray-50 group/item border-l-4 transition-colors ${
        item.status ? 'border-l-green-500 bg-green-50/30' : 'border-l-transparent'
      } ${dragOverIndex === index ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
      data-testid={`checklist-item-${index}`}
      draggable
      onDragStart={(e) => onDragStartItem && onDragStartItem(e, index)}
      onDragOver={(e) => onDragOverItem && onDragOverItem(e, index)}
      onDragLeave={onDragLeaveItem}
      onDrop={(e) => onDropItem && onDropItem(e, index)}
    >
      {/* Cabeçalho do item (sempre visível, clicável para expandir) */}
      <div className="flex items-start gap-2">
        <GripVertical
          className="h-4 w-4 mt-1 text-gray-300 group-hover/item:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
          data-testid={`item-drag-handle-${index}`}
        />
        <Checkbox
          checked={item.status || false}
          onCheckedChange={(checked) => onUpdateItem(index, 'status', checked)}
          className="mt-0.5"
          data-testid={`item-checkbox-${index}`}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 text-left"
          data-testid={`item-toggle-${index}`}
        >
          <div className="flex items-center gap-2 flex-wrap relative">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
            )}
            <p className={`font-medium ${item.status ? 'text-green-700' : 'text-gray-900'}`}>
              {item.status && <span className="mr-1">✓</span>}
              {item.descricao || item.item}
            </p>
            <Badge
              className={`text-xs cursor-pointer hover:opacity-80 transition-opacity ${tipoInfo.color}`}
              onClick={(e) => { e.stopPropagation(); setShowTipoMenu(!showTipoMenu); }}
              data-testid={`item-tipo-badge-${index}`}
            >
              <TipoIcon className="h-3 w-3 mr-1" />
              {tipoInfo.label}
            </Badge>
            {showTipoMenu && (
              <div ref={tipoMenuRef} className="absolute z-20 mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]" onClick={(e) => e.stopPropagation()}>
                {Object.entries(TIPOS_ITEM).map(([key, info]) => {
                  const Icon = info.icon;
                  const isActive = (item.tipo_item || 'especificacao') === key;
                  return (
                    <button
                      key={key}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors ${isActive ? 'bg-gray-50 font-semibold' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateItem(index, 'tipo_item', key);
                        setShowTipoMenu(false);
                      }}
                      data-testid={`item-tipo-option-${key}-${index}`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${info.color.split(' ')[0]}`} />
                      <span>{info.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {podeVerCustos && custoItem > 0 && (
              <Badge variant="outline" className="text-xs text-green-700 bg-green-50">
                {formatCurrency(custoItem)}
              </Badge>
            )}
            {item.status && (
              <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-300">
                ✓ Definido
              </Badge>
            )}
          </div>
          {/* Sub-linha resumida (visivel quando colapsado) */}
          {!expanded && (
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 ml-6 flex-wrap">
              {fornecedorSel ? (
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  {fornecedorSel.nome}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-yellow-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                  Sem fornecedor
                </span>
              )}
              {itensCusto.length > 0 && (
                <span className="flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  {itensCusto.length} produto{itensCusto.length > 1 ? 's' : ''} vinculado{itensCusto.length > 1 ? 's' : ''}
                </span>
              )}
              {item.observacao && (
                <span className="flex items-center gap-1 truncate max-w-xs">
                  <FileText className="h-3 w-3" />
                  {item.observacao.slice(0, 50)}{item.observacao.length > 50 ? '…' : ''}
                </span>
              )}
              {temFoto && (
                <span className="flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  1 foto
                </span>
              )}
            </div>
          )}
        </button>
        {/* Botao remover item */}
        {onRemoveItem && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0 text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover/item:opacity-100 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onRemoveItem(index); }}
            title="Remover este item do projeto"
            data-testid={`item-remove-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Corpo expandido — só renderiza quando expanded=true */}
      {expanded && (
        <div className="mt-4 ml-8 space-y-4" data-testid={`item-body-${index}`}>
          {/* 1. Produtos vinculados */}
          {podeVerCustos && (
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-gray-600 font-medium flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  Selecione os produtos que deseja vincular a este item:
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => onOpenAddProduto(index)}
                >
                  <Package className="h-3 w-3 mr-1" />
                  + Produto
                </Button>
              </div>
              {itensCusto.length > 0 ? (
                <div className="space-y-2">
                  {itensCusto.map((custo, custoIdx) => (
                    <div key={(custo && custo.id) || custoIdx} className="flex flex-wrap items-center gap-2 p-2 bg-white rounded border text-sm">
                      {/* Linha do produto: nome ocupa toda a largura em telas estreitas, controles vão pra próxima linha */}
                      <div className="flex items-center gap-2 flex-1 basis-full sm:basis-auto min-w-0">
                        {custo?.tipo === 'produto' ? (
                          <Package className="h-4 w-4 text-green-600 shrink-0" />
                        ) : (
                          <Wrench className="h-4 w-4 text-orange-600 shrink-0" />
                        )}
                        <span className="flex-1 truncate min-w-0" title={custo?.produto_nome || custo?.descricao_manual}>
                          {custo?.produto_nome || custo?.descricao_manual}
                        </span>
                      </div>
                      {/* Controles: qty x valor = total + remover */}
                      <div className="flex items-center gap-2 ml-auto shrink-0">
                        <Input
                          type="number"
                          value={custo?.quantidade}
                          onChange={(e) => onUpdateItemCusto(index, custoIdx, 'quantidade', e.target.value)}
                          className="w-16 h-7 text-xs"
                          min="0"
                          step="0.01"
                          data-testid={`custo-qty-${index}-${custoIdx}`}
                        />
                        <span className="text-xs text-gray-500">×</span>
                        <CurrencyInput
                          value={(custo && custo.valor_unitario) || 0}
                          onChange={(v) => onUpdateItemCusto(index, custoIdx, 'valor_unitario', v)}
                          className="w-24 h-7 text-xs"
                        />
                        <span className="font-medium text-green-700 text-right whitespace-nowrap text-xs">
                          = {formatCurrency((custo && custo.valor_total) || 0)}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                          onClick={() => onRemoveItemCusto(index, custoIdx)}
                          title="Desvincular este produto"
                          data-testid={`custo-remove-${index}-${custoIdx}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      {/* Comprado (custo real via pedidos de compra) */}
                      {Array.isArray(custo?.compras) && custo.compras.length > 0 && (
                        <div className="basis-full flex items-center gap-2 flex-wrap mt-1 pl-6" data-testid={`custo-comprado-${index}-${custoIdx}`}>
                          <Badge variant="outline" className="text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200">
                            Comprado: {custo.qtd_comprada} × {formatCurrency(custo.valor_real_unitario || 0)} = {formatCurrency(custo.valor_real_total || 0)}
                          </Badge>
                          {(() => {
                            const plan = parseFloat(custo.valor_unitario || 0);
                            const real = parseFloat(custo.valor_real_unitario || 0);
                            if (!plan || !real) return null;
                            const pct = ((real - plan) / plan) * 100;
                            if (Math.abs(pct) < 0.05) {
                              return <Badge className="text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-100">= planejado</Badge>;
                            }
                            return (
                              <Badge className={`text-[10px] ${pct < 0 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
                                {pct < 0 ? '▼' : '▲'} {Math.abs(pct).toFixed(1)}% vs planejado
                              </Badge>
                            );
                          })()}
                          {custo.compras.map((c, i) => (
                            <span key={i} className="text-[10px] text-gray-500 flex items-center gap-1">
                              <Link2 className="h-3 w-3" />
                              {c.pedido_numero}{c.fornecedor_nome ? ` · ${c.fornecedor_nome}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-2">
                  Nenhum produto vinculado ainda
                </p>
              )}
              <EstoqueStatus
                item={item}
                projeto={projeto}
                embarcacaoIndex={embarcacaoIndex}
                onOpenSolicitarCompra={onOpenSolicitarCompra}
                onReservaSuccess={() => {}}
              />
            </div>
          )}

          {/* 2. Fornecedor */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-600 font-medium">Fornecedor</Label>
            <FornecedorCombobox
              value={item.fornecedor_id || ''}
              onChange={(id) => onUpdateItem(index, 'fornecedor_id', id || '')}
              fornecedores={fornecedores}
              onFornecedoresUpdate={onFornecedoresUpdate}
              placeholder="Buscar fornecedor..."
              testIdPrefix={`fornecedor-item-${index}`}
            />
          </div>

          {/* 3. Observação */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-600 font-medium flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Observação
            </Label>
            <Textarea
              value={item.observacao || ''}
              onChange={(e) => onUpdateItem(index, 'observacao', e.target.value)}
              placeholder="Adicione aqui informações complementares sobre este item"
              className="text-sm min-h-[60px]"
              data-testid={`item-observacao-${index}`}
            />
          </div>

          {/* 4. Quantidade / Modelo */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-600 font-medium">Quantidade / Modelo</Label>
            <Input
              value={item.quantidade || ''}
              onChange={(e) => onUpdateItem(index, 'quantidade', e.target.value)}
              placeholder="Caso precise especificar quantidades e modelos de produtos não vinculados, utilize este campo"
              className="text-sm"
              data-testid={`item-quantidade-${index}`}
            />
          </div>

          {/* 5. Anexo de imagens */}
          <div className="border rounded-lg p-3 bg-blue-50/40">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Paperclip className="h-4 w-4 text-blue-600" />
                  <Label className="text-xs text-gray-600 font-medium">Anexo de imagens</Label>
                </div>
                {!item.foto_url && (
                  <ImageUpload
                    value={item.foto_url || ''}
                    onChange={(url) => onUpdateItem(index, 'foto_url', url)}
                    onRemove={() => onUpdateItem(index, 'foto_url', '')}
                    placeholder="Anexar foto de referência..."
                    className="max-w-xs"
                  />
                )}
              </div>
              {item.foto_url && (
                <div className="relative shrink-0">
                  <div
                    className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-100 group cursor-pointer"
                    onClick={() => setImagemAmpliada(getImageUrl(item.foto_url))}
                  >
                    <img
                      src={getImageUrl(item.foto_url)}
                      alt="Foto do item"
                      className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiI+PHJlY3QgeD0iMyIgeT0iMyIgd2lkdGg9IjE4IiBoZWlnaHQ9IjE4IiByeD0iMiIgcnk9IjIiLz48Y2lyY2xlIGN4PSI4LjUiIGN5PSI4LjUiIHI9IjEuNSIvPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiLz48L3N2Zz4=';
                        e.target.className = 'w-full h-full object-contain p-4';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                      <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateItem(index, 'foto_url', '');
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
              <Dialog open={!!imagemAmpliada} onOpenChange={() => setImagemAmpliada(null)}>
                <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
                  <button
                    onClick={() => setImagemAmpliada(null)}
                    className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  {imagemAmpliada && (
                    <img
                      src={imagemAmpliada}
                      alt="Imagem ampliada"
                      className="w-full h-auto max-h-[85vh] object-contain"
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemChecklist;
